import {
    useMemo, useCallback, useState,
} from 'react'
import noop from 'lodash/noop'
import {
    DetailPanelComponentProps,
} from 'app/shared-components/SkyNetDataGridExpandable'
import StatusHandler from 'app/shared-components/StatusHandler'
import {
    AdditionalFeesStatus,
} from 'app/enums/label.enums'
import ConfirmationDialog from 'app/shared-components/ConfirmationDialog'
import Button from 'app/shared-components/Button'
import {
    useChangeValidity,
} from 'app/shared-components/ChangeValidity'
import {
    ArchiveButton, ArchiveConfirmationDialog, useArchive,
} from 'app/shared-components/Archive'
import {
    WithId,
} from 'app/types/common.types'

import useHasPermission from 'app/hooks/useHasPermission'
import useRequestWithConfirmation from 'app/hooks/useRequestWithConfirmation'
import AdditionalFeesRequests from 'app/requests/ContractBasis/AdditionalFees.requests'

import AdditionalFeesForm from 'app/Apps/Pricing/ContractBasis/AdditionalFees/AdditionalFeesForm'
import AdditionalFeeChangeValidity from 'app/Apps/Pricing/ContractBasis/AdditionalFees/AdditionalFeeChangeValidity'
import AdditionalFeeCancellation from 'app/Apps/Pricing/ContractBasis/AdditionalFees/AdditionalFeeCancellation'
import useGetAdditionalFeesById from 'app/Apps/Pricing/ContractBasis/AdditionalFees/hooks/useGetAdditionalFeesById'

export default function AdditionalFeesUpdate({
    id, onSuccess, onCopy, onCancel,
}: Readonly<DetailPanelComponentProps & WithId<number>>) {
    const contractBasisUpdatePermission = useHasPermission(['contract_basis_update'])

    const [
        reason,
        setReason,
    ] = useState('')

    const {
        data,
        isDataReady,
        isFetching,
        isError,
        error,
        invalidate,
    } = useGetAdditionalFeesById(id)

    const handleSuccess = useCallback(({
        id: updatedId,
    }: WithId<number | string>) => {
        onSuccess({
            id: updatedId,
        })
        invalidate()
    }, [
        invalidate,
        onSuccess,
    ])

    const onArchive = useCallback(() => {
        onSuccess({
            toCollapse: true,
        })
        invalidate()
    }, [
        invalidate,
        onSuccess,
    ])

    const requestParams = useMemo(() => {
        if (data?.additionalFeesStatus !== AdditionalFeesStatus.IN_PROGRESS) {
            return AdditionalFeesRequests.editableData({
                id,
            })
        }

        return AdditionalFeesRequests.update({
            id,
        })
    }, [
        data?.additionalFeesStatus,
        id,
    ])

    const changeValidity = useChangeValidity({
        requestParamFn: AdditionalFeesRequests.validity,
        notificationId: 'AdditionalFeesUpdateDto',
        title: 'Additional Fee',
        data: {
            id: data?.id,
            validFrom: data?.validFrom,
            validTo: data?.validTo,
        },
        onSuccess: handleSuccess,
    })

    const cancelFee = useRequestWithConfirmation({
        requestParamFn: AdditionalFeesRequests.cancellation,
        notificationId: 'AdditionalFeesUpdateDto',
        title: 'Additional Fee',
        data: {
            ...data,
            commentForBilling: reason,
        },
        actionDone: 'Cancelled',
        onSuccess: handleSuccess,
    })

    const validFee = useRequestWithConfirmation({
        requestParamFn: AdditionalFeesRequests.validation,
        notificationId: 'AdditionalFeeUpdateDto',
        title: 'Additional Fee',
        data: {
            ...data,
            commentForBilling: reason,
        },
        actionDone: 'Validated',
        onSuccess: handleSuccess,
    })

    const archive = useArchive({
        requestParamFn: data?.archived ? AdditionalFeesRequests.restore
            : AdditionalFeesRequests.archive,
        notificationId: 'AdditionalFeeUpdateDto',
        title: 'Additional Fee',
        data,
        redirectionAfterSuccess: noop, // TODO: use generateSkyNetLink when move to real endpoins
        onSuccess: onArchive,
    })

    const customButtonsDisabled = useMemo(() => {
        return data?.additionalFeesStatus === AdditionalFeesStatus.CANCELLED
            || data?.additionalFeesStatus === AdditionalFeesStatus.VALID
    }, [data])

    const customButtons = useMemo(() => {
        const buttons = []

        if (contractBasisUpdatePermission) {
            buttons.push(
                <Button
                    key="Copy"
                    name="Copy"
                    label="Copy"
                    onClick={onCopy}
                />,
                <Button
                    key="Set to Valid"
                    name="Set to Valid"
                    label="Set to Valid"
                    onClick={validFee.askConfirmation}
                    disabled={customButtonsDisabled}
                />,
                <Button
                    key="Set to Canceled"
                    name="Set to Canceled"
                    label="Set to Canceled"
                    onClick={cancelFee.askConfirmation}
                    disabled={data?.additionalFeesStatus === AdditionalFeesStatus.CANCELLED}
                />,
                <Button
                    key="Change Validity"
                    name="Change Validity"
                    label="Change Validity"
                    onClick={changeValidity.handleChangeValidity}
                    disabled={data?.additionalFeesStatus !== AdditionalFeesStatus.VALID}
                />,
            )
            if (data?.additionalFeesStatus === AdditionalFeesStatus.CANCELLED) {
                buttons.push(
                    <ArchiveButton
                        key="archive-button"
                        onClick={archive.handleArchiveRestore}
                        archived={data?.archived}
                    />,
                )
            }
        }
        return buttons
    }, [
        contractBasisUpdatePermission,
        validFee.askConfirmation,
        customButtonsDisabled,
        cancelFee.askConfirmation,
        data?.additionalFeesStatus,
        data?.archived,
        changeValidity.handleChangeValidity,
        onCopy,
        archive.handleArchiveRestore,
    ])

    return (
        <StatusHandler
            isSuccess={isDataReady}
            isFetching={isFetching}
            isError={isError}
            error={error}
        >
            <>
                <AdditionalFeesForm
                    disabled={!contractBasisUpdatePermission}
                    value={data}
                    onSuccess={handleSuccess}
                    onCancel={onCancel}
                    requestParams={requestParams}
                    customButtons={customButtons}
                />
                <AdditionalFeeChangeValidity
                    changeValidity={changeValidity}
                    id={data?.id}
                />
                <AdditionalFeeCancellation
                    id={data?.id}
                    cancelFee={cancelFee}
                    setReason={setReason}
                    reason={reason}
                />
                <ArchiveConfirmationDialog
                    title="Additional Fee"
                    openConfirmDialog={archive.openConfirmDialog}
                    handleClose={archive.handleArchiveRestoreConfirm}
                    archived={data?.archived}
                />
                <ConfirmationDialog
                    open={validFee.openConfirmDialog}
                    handleClose={validFee.doAction}
                    dialogTitle="VALIDATE ADDITIONAL FEE CONFIRMATION"
                    dialogContent="Do you want to validate this Additional Fee?"
                />
            </>
        </StatusHandler>
    )
}
